/**
 * Counting what an admissions form counts.
 *
 * Application forms disagree about what a "character" and a "word" are, and the
 * disagreement is the whole reason these tools exist. The UCAS personal statement counts
 * characters, spaces included; Common App counts words. Neither publishes the corner
 * cases (a line break, a dash with spaces round it), so the helpers here state the rule
 * they use and let the reader see the alternative.
 */

const LINE_ENDINGS = /\r\n?|[\u2028\u2029]/g;
const WHITESPACE = /\s+/;
const HAS_LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const SENTENCE_BOUNDARY = /(?<=[.!?…]["'”’)\]]*)\s+/u;
const BLANK_LINES = /\n\s*\n/;

/** Line endings of every kind become a single "\n", as a text box stores them. */
export function normalizeLineEndings(text: string): string {
  return text.replace(LINE_ENDINGS, "\n");
}

/**
 * Words as a counter reads them: runs of non-space that contain a letter or digit.
 *
 * A dash or ampersand standing alone between spaces is punctuation, not a word, so
 * "work — and play" is three words. A hyphenated compound is one word, and a number is
 * one word. Microsoft Word counts a lone dash as a word; the difference is a word or two
 * in a 650-word essay, which is why the page says to leave a margin.
 */
export function countWords(text: string): number {
  let count = 0;
  for (const token of text.split(WHITESPACE)) {
    if (HAS_LETTER_OR_DIGIT.test(token)) {
      count += 1;
    }
  }
  return count;
}

/** Characters a reader sees, as code points, so an emoji or a CJK character is one. */
export function countCharacters(text: string): number {
  return [...normalizeLineEndings(text)].length;
}

/**
 * Characters as a form with a character limit counts them: every character including
 * spaces, and each line break costing `lineBreakCost` (1 for a bare "\n"; 2 if the form
 * stores Windows-style "\r\n").
 */
export function countWithBreaks(text: string, lineBreakCost: 1 | 2): number {
  const normalized = normalizeLineEndings(text);
  const breaks = normalized.split("\n").length - 1;
  return [...normalized].length - breaks + breaks * lineBreakCost;
}

/** How many line breaks the text contains. */
export function countLineBreaks(text: string): number {
  return normalizeLineEndings(text).split("\n").length - 1;
}

/** Paragraphs separated by a blank line, or by a single line break when none is blank. */
export function splitParagraphs(text: string): string[] {
  const normalized = normalizeLineEndings(text).trim();
  if (normalized === "") {
    return [];
  }
  const parts = BLANK_LINES.test(normalized)
    ? normalized.split(BLANK_LINES)
    : normalized.split("\n");
  return parts.map((part) => part.trim()).filter((part) => part !== "");
}

/** Sentences, split after . ! ? or … followed by a space. Good enough for a length check. */
export function splitSentences(text: string): string[] {
  return normalizeLineEndings(text)
    .split(SENTENCE_BOUNDARY)
    .map((part) => part.replace(WHITESPACE, " ").trim())
    .filter((part) => part !== "");
}

/** Whole number percentage of `part` in `whole`, 0 when `whole` is 0. */
export function percentOf(part: number, whole: number): number {
  return whole <= 0 ? 0 : Math.round((part / whole) * 100);
}

export interface TextSection {
  heading: string;
  body: string;
}

/** Sections as a plain-text file: heading, then body; sections with no body are skipped. */
export function joinSections(sections: readonly TextSection[]): string {
  return sections
    .filter((section) => section.body.trim() !== "")
    .map((section) => `${section.heading}\n${section.body.trim()}`)
    .join("\n\n\n");
}
